import React, { Fragment, useMemo, useState } from 'react';
import { LoadingSpinner } from '../../../../constants/AppLoading';
import AppTable from '../../../../constants/AppTable';
import { TapSize, UnitRate } from '../../../../helpers/models/Models';
import DialogBox from '../../../../constants/DialogBox';
import { Grid } from '@mui/material';
import InputTextBox from '../../../../constants/InputTextBox';
import { useFormik } from 'formik';
import { putDataToTheServer } from '../../../../helpers/api';
import DefaultSelectInputBox from '../../../../constants/DefaultSelectBox';
import { MeterTypeOptions, TapSizeOptions, YesNoOptions } from '../../../../constants/Options';
import { EndBaseLine } from '../../../../constants/AppSpace';
import { Button } from '../../../../constants/Button';
import AppURL from '../../../../constants/AppURL';
import { ToastSuccess, ToastWarning } from '../../../../constants/Toast';
import SelectionBox from '../../../../constants/SelectionBox';

interface TariffParameterListViewProps {
    results: {
        results: UnitRate[];
        previous: string | null;
        next: string | null;
    } | null;
    loading: boolean;
    tap_loading: boolean;
    tap_list: {
        results: TapSize[];
        previous: string | null;
        next: string | null;
    } | null;
    error: string | null;
    setOnURL: React.Dispatch<React.SetStateAction<string>>;
    handleFormSuccess: () => void;
}

const TariffParameterListView: React.FC<TariffParameterListViewProps> = ({ handleFormSuccess, tap_list, tap_loading, results, loading, error, setOnURL }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<UnitRate | null>(null);
    const tapSizeOptions = TapSizeOptions(tap_list?.results);

    const columns = [
        { header: 'SN', accessor: 'sn', sortable: true },
        { header: 'Tap Type', accessor: 'tap_type', sortable: true },
        { header: 'Tap Size Name', accessor: 'tap_size_name', sortable: true },
        { header: 'From Unit', accessor: 'unit_from', sortable: false },
        { header: 'To Unit', accessor: 'unit_to', sortable: false },
        { header: 'Unit Amt', accessor: 'unit_amt', sortable: false },
        { header: 'Per Unit', accessor: 'per_unit', sortable: false },

        { header: 'Actions', accessor: 'actions', action: true },
    ];

    const modifiedData = useMemo(() => {
        if (!results) return [];
        return results.results.map((item: UnitRate) => ({
            sn: item.sn,
            tap_size_name: item.tap_size_name,
            unit_from: item.unit_from,
            unit_to: item.unit_to,
            tap_type: item.tap_type === 1 ? "Personal" : "Organization",
            tap_size: item.tap_size,  // Assuming status is boolean
            per_unit: item.per_unit === 1 ? "Yes" : "No",  // Assuming status is boolean
            remarks: item.remarks,  // Assuming status is boolean
            created_at: item.created_at,  // Assuming status is boolean
            updated_at: item.updated_at,  // Assuming status is boolean
            unit_amt: item.unit_amt,  // Assuming status is boolean
            actions: (
                <button onClick={() => handleEdit(item)}>Edit</button>
            ),
        }));
    }, [results]);



    const handleEdit = (item: UnitRate) => {
        setCurrentItem(item);
        setDialogOpen(true);
    };
    const formik = useFormik({
        initialValues: {
            tap_size_name: currentItem?.tap_size_name || "",
            unit_from: currentItem?.unit_from || "0",
            unit_to: currentItem?.unit_to || "0",
            tap_type: currentItem?.tap_type === "Personal" ? 1 : 2,
            tap_size: currentItem?.tap_size || "",
            per_unit: currentItem?.per_unit === 1 ? "1" : "2",
            remarks: currentItem?.remarks || "",
            unit_amt: currentItem?.unit_amt || "",
        },
        // validationSchema: toleValidationSchema,
        enableReinitialize: true,
        onSubmit: async (values, action) => {
            const formData = new FormData();
            formData.append("tap_type", values.tap_type?.toString());
            formData.append("tap_size", values.tap_size?.toString());
            formData.append("unit_from", values.unit_from?.toString());
            formData.append("unit_to", values.unit_to?.toString());
            formData.append("unit_amt", values.unit_amt?.toString());
            formData.append("remarks", values.remarks);
            formData.append("per_unit", values.per_unit === "1" ? "1" : "0");
            try {
                await putDataToTheServer(AppURL.AccountURL + 'account/unit/rate/retrieve/update/view/' + currentItem?.sn, formData).then((res) => {
                    if (res?.status === 200) {
                        handleFormSuccess();
                        setDialogOpen(false);
                        ToastSuccess({ message: res?.message });
                    } else if (res?.status === 400) {
                        ToastWarning({ message: res?.message });
                    } else {
                        ToastWarning({ message: "Some Issue" });
                    }
                });
            } catch (error) {
                console.log(error);
            }
        },
    });

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return <p>Error loading data: {error}</p>;
    }


    return (
        <Fragment>
            <AppTable
                columns={columns}
                data={modifiedData}
                onEdit={handleEdit}
                onNext={() => results?.next && setOnURL(results.next)}
                onPrev={() => results?.previous && setOnURL(results.previous)}
            />
            {dialogOpen && currentItem && (
                <DialogBox open={dialogOpen} headerName='Tariff Parameter Edit Update' close={() => setDialogOpen(false)}>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container columnSpacing={3} rowSpacing={1}>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <SelectionBox
                                    name="tap_type"
                                    label='Tap Type'
                                    disabled={false} // Adjust based on your needs
                                    options={MeterTypeOptions}
                                    value={formik.values.tap_type}
                                    handleChange={(name: any, selectedOption: any) => {
                                        formik.setFieldValue(name, selectedOption.value);
                                    }
                                    }
                                    placeholder="Select Tap type"
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <SelectionBox
                                    name="tap_size"
                                    label='Tap Size'
                                    disabled={false} // Adjust based on your needs
                                    options={tapSizeOptions}
                                    value={formik.values.tap_size}
                                    handleChange={(name: any, selectedOption: any) => {
                                        formik.setFieldValue(name, selectedOption.value);
                                    }
                                    }
                                    placeholder="Select Tap Size"
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='number'
                                    name='unit_from'
                                    label='From Unit'
                                    handleChange={formik.handleChange}
                                    value={formik.values.unit_from?.toString()}
                                    error={formik.errors.unit_from}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.unit_from}
                                    required={true}
                                    placeholder='XX'
                                />
                            </Grid>

                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='number'
                                    name='unit_to'
                                    label='To Unit'
                                    handleChange={formik.handleChange}
                                    value={formik.values.unit_to?.toString()}
                                    error={formik.errors.unit_to}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.unit_to}
                                    required={true}
                                    placeholder='10'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='number'
                                    name='unit_amt'
                                    label='Unit Amount'
                                    handleChange={formik.handleChange}
                                    value={formik.values.unit_amt?.toString()}
                                    error={formik.errors.unit_amt}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.unit_amt}
                                    required={true}
                                    placeholder='XX'
                                />

                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <SelectionBox
                                    name="per_unit"
                                    label='Per Unit'
                                    disabled={false} // Adjust based on your needs
                                    options={YesNoOptions}
                                    value={formik.values.per_unit}
                                    handleChange={(name: any, selectedOption: any) => {
                                        formik.setFieldValue(name, selectedOption.value);
                                    }
                                    }
                                    placeholder="Select Per Unit"
                                />
                            </Grid>
                            <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <EndBaseLine top={18}>
                                    <Button btn_type='update' type='submit' el='button' />
                                </EndBaseLine>
                            </Grid>
                        </Grid>
                    </form>
                </DialogBox>
            )}
        </Fragment>
    );
};

export default TariffParameterListView;
